"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.referralReportQuerySchema = exports.globalSearchQuerySchema = exports.SEARCH_MATCH_KINDS = exports.SEARCH_TYPE_LABELS = exports.SEARCH_TYPES = exports.SEARCH_TYPEAHEAD_PER_TYPE = exports.SEARCH_DEBOUNCE_MS = exports.SEARCH_MIN_CHARS = exports.candidateSearchSchema = exports.POOL_FILTERS = exports.listParam = exports.renameTagSchema = exports.MERGE_CONFIRM_WORD = exports.mergeIntoSchema = exports.poolMatchThresholdSchema = exports.invitePoolCandidatesSchema = exports.poolMembershipsActionSchema = exports.addPoolMembersSchema = exports.poolVersionSchema = exports.updatePoolSchema = exports.createPoolSchema = exports.poolNameSchema = exports.POOL_MATCH_EXCLUSION_LABELS = exports.POOL_MATCH_EXCLUSIONS = exports.POOL_INVITE_LIMIT = exports.POOL_BULK_LIMIT = exports.TALENT_POOL_SOURCE_LABEL = exports.TALENT_POOL_SOURCE = exports.GENERAL_POOL_NAME = exports.GENERAL_POOL_KEY = exports.POOL_COOLDOWN_MONTHS = exports.POOL_COOLDOWN_NO_SHOWS = exports.POOL_MATCH_DEFAULT_THRESHOLD = exports.POOL_FRESHNESS_MONTHS = exports.POOL_CONSENT_WARNING_DAYS = exports.POOL_MEMBERSHIP_MONTHS = void 0;
exports.membershipExpiry = membershipExpiry;
exports.isConsentExpiringSoon = isConsentExpiringSoon;
exports.poolFreshness = poolFreshness;
exports.cooldownUntil = cooldownUntil;
exports.poolMatchExclusion = poolMatchExclusion;
exports.generalCvFormDefinition = generalCvFormDefinition;
exports.stageReached = stageReached;
exports.referralOutcome = referralOutcome;
const zod_1 = require("zod");
const calendars_1 = require("./calendars");
const enums_1 = require("./enums");
const jobs_1 = require("./jobs");
const schemas_1 = require("./schemas");
// S25 — talent pools, pool match, advanced search, global search and the referral report:
// POOL-01–04, ADMIN-11; F14.01–F14.09, F20.04. Decisions: DECISIONS-LOG S25–S28 Q1–Q6.
//
// Responses keep people under `candidate` keys, so the serialiser masks their §L23 fields
// (non-negotiable #5) — a pool member's phone is `—` for anyone who may not see it.
/** Q1 (O12): consent in a pool lasts 24 months from joining it or its last renewal. */
exports.POOL_MEMBERSHIP_MONTHS = 24;
/** §L22 N-64: the owner hears 30 days before a member's consent runs out. */
exports.POOL_CONSENT_WARNING_DAYS = 30;
/** E12 "freshness": the share of members contacted in the last six months. */
exports.POOL_FRESHNESS_MONTHS = 6;
/** §L17: "Threshold: default 60; configurable per job." */
exports.POOL_MATCH_DEFAULT_THRESHOLD = 60;
/**
 * §L17 excludes `cooldown_until > today`; nothing in the system sets such a date. F10.11 says
 * two interview no-shows suggest a cooldown, and §L14's default waiting period is six months —
 * so two or more no-shows put a person in cooldown for six months after the last (ADR 0027).
 */
exports.POOL_COOLDOWN_NO_SHOWS = 2;
exports.POOL_COOLDOWN_MONTHS = 6;
/** Q4: the pool a "Send us your CV" lands in, made by the system and owned by the HR Leads. */
exports.GENERAL_POOL_KEY = 'general_cvs';
exports.GENERAL_POOL_NAME = 'General CVs';
/** §L17: an invited application's source. */
exports.TALENT_POOL_SOURCE = 'talent_pool';
exports.TALENT_POOL_SOURCE_LABEL = 'Talent pool';
/** §L9: the most records one bulk action may touch. */
exports.POOL_BULK_LIMIT = 200;
/** One invitation round from a match panel; each is an email and an application. */
exports.POOL_INVITE_LIMIT = 50;
// --- Pure rules (unit-tested) ------------------------------------------------------------------
/** When consent runs out for a membership made or renewed at `from` (Q1). */
function membershipExpiry(from) {
    const next = new Date(from.getTime());
    next.setUTCMonth(next.getUTCMonth() + exports.POOL_MEMBERSHIP_MONTHS);
    return next;
}
/** True from 30 days before expiry until it lapses — the N-64 window. */
function isConsentExpiringSoon(expiresAt, now) {
    const warnFrom = expiresAt.getTime() - exports.POOL_CONSENT_WARNING_DAYS * DAY_MS;
    return now.getTime() >= warnFrom && now.getTime() < expiresAt.getTime();
}
/** E12: % of members contacted in the last six months; null for an empty pool. */
function poolFreshness(lastContacts, now) {
    if (lastContacts.length === 0)
        return null;
    const since = monthsBefore(now, exports.POOL_FRESHNESS_MONTHS).getTime();
    const fresh = lastContacts.filter((contacted) => contacted !== null && contacted.getTime() >= since).length;
    return Math.round((fresh / lastContacts.length) * 100);
}
/** The date a person's cooldown ends, or null when they are not in one (see POOL_COOLDOWN_*). */
function cooldownUntil(noShowDates) {
    if (noShowDates.length < exports.POOL_COOLDOWN_NO_SHOWS)
        return null;
    const latest = Math.max(...noShowDates.map((date) => date.getTime()));
    const until = new Date(latest);
    until.setUTCMonth(until.getUTCMonth() + exports.POOL_COOLDOWN_MONTHS);
    return until;
}
exports.POOL_MATCH_EXCLUSIONS = ['do_not_rehire', 'cooldown', 'consent_expired', 'already_applied', 'anonymised'];
exports.POOL_MATCH_EXCLUSION_LABELS = {
    do_not_rehire: 'Do not rehire',
    cooldown: 'In cooldown after two no-shows',
    consent_expired: 'Consent expired',
    already_applied: 'Already applied to this job',
    anonymised: 'Record anonymised',
};
/** §L17's exclusions, in the order a recruiter would want to read them; null = eligible. */
function poolMatchExclusion(person, now) {
    if (person.isAnonymised)
        return 'anonymised';
    if (person.doNotRehire)
        return 'do_not_rehire';
    if (person.hasAppliedToJob)
        return 'already_applied';
    if (person.consentExpiresAt.getTime() <= now.getTime())
        return 'consent_expired';
    const cooldown = cooldownUntil(person.noShowDates);
    if (cooldown && cooldown.getTime() > now.getTime())
        return 'cooldown';
    return null;
}
const DAY_MS = 24 * 60 * 60 * 1000;
function monthsBefore(now, months) {
    const date = new Date(now.getTime());
    date.setUTCMonth(date.getUTCMonth() - months);
    return date;
}
// --- The career site's "Send us your CV" (S14 Q6, S25 Q4) -----------------------------------
/** "Standard fields" (master plan S25): who they are, how to reach them, and the CV. */
const GENERAL_CV_VISIBILITY = {
    full_name: 'required',
    email: 'required',
    phone: 'required',
    cv: 'required',
    city: 'optional',
    current_title: 'optional',
    total_experience: 'optional',
    highest_education: 'optional',
    skills: 'optional',
};
/** The same form machinery a job uses, so the §L2 check and the parsing cannot differ. */
function generalCvFormDefinition() {
    const formFields = Object.fromEntries(jobs_1.STANDARD_FIELDS.map((field) => [field.key, GENERAL_CV_VISIBILITY[field.key] ?? 'hidden']));
    return (0, jobs_1.buildPublicFormDefinition)({ jobId: exports.GENERAL_POOL_KEY, title: 'Send us your CV', formFields, criteria: [], questions: [] });
}
// --- Input -------------------------------------------------------------------------------------
const optionalText = (max, message) => zod_1.z
    .string()
    .trim()
    .max(max, message ?? `Keep it under ${max} characters.`)
    .transform((value) => (value === '' ? null : value))
    .nullable()
    .default(null);
exports.poolNameSchema = zod_1.z.string().trim().min(2, 'Give the pool a name of at least 2 characters.').max(80, 'Keep the name under 80 characters.');
exports.createPoolSchema = zod_1.z.object({
    name: exports.poolNameSchema,
    description: optionalText(400),
    /** Null = the person creating it. */
    ownerUserId: schemas_1.uuidSchema.nullable().default(null),
});
exports.updatePoolSchema = zod_1.z.object({
    name: exports.poolNameSchema,
    description: optionalText(400),
    ownerUserId: schemas_1.uuidSchema.nullable().default(null),
    version: zod_1.z.number().int().positive(),
});
exports.poolVersionSchema = zod_1.z.object({ version: zod_1.z.number().int().positive() });
/** F14.02 — from search results, the profile, or a pool's own "Add people". */
exports.addPoolMembersSchema = zod_1.z.object({
    candidateIds: zod_1.z.array(schemas_1.uuidSchema).min(1, 'Choose at least one person.').max(exports.POOL_BULK_LIMIT, `Choose ${exports.POOL_BULK_LIMIT} people or fewer.`),
    note: optionalText(500),
    /** The application that led here, when there is one (E12 "from which application"). */
    sourceApplicationId: schemas_1.uuidSchema.nullable().default(null),
});
/** F14.03 remove, F14.07 consent renewed — on one or many memberships of a pool. */
exports.poolMembershipsActionSchema = zod_1.z.object({
    membershipIds: zod_1.z.array(schemas_1.uuidSchema).min(1, 'Choose at least one person.').max(exports.POOL_BULK_LIMIT, `Choose ${exports.POOL_BULK_LIMIT} people or fewer.`),
    note: optionalText(500),
});
/** F14.05 — Q3: the application is made at once, and the re-engagement email goes. */
exports.invitePoolCandidatesSchema = zod_1.z.object({
    jobId: schemas_1.uuidSchema,
    candidateIds: zod_1.z.array(schemas_1.uuidSchema).min(1, 'Choose at least one person.').max(exports.POOL_INVITE_LIMIT, `Invite ${exports.POOL_INVITE_LIMIT} people or fewer at a time.`),
});
exports.poolMatchThresholdSchema = zod_1.z.object({
    threshold: zod_1.z.number().int('Use a whole number.').min(0, 'Use a number from 0 to 100.').max(100, 'Use a number from 0 to 100.'),
});
/**
 * ADMIN-11. `version` is the merged-away pool's or tag's, as the page showed it (Admin redesign 2026-11-02): a merge is
 * never undone, so one from a stale page is refused rather than run on what someone else changed since.
 */
exports.mergeIntoSchema = zod_1.z.object({ intoId: schemas_1.uuidSchema, version: zod_1.z.number().int().positive() });
/** The word typed to confirm a merge, which can't be undone (06-qol §4). */
exports.MERGE_CONFIRM_WORD = 'merge';
exports.renameTagSchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(1, 'Give the tag a name.').max(60, 'Keep the tag under 60 characters.'),
    version: zod_1.z.number().int().positive(),
});
// --- POOL-04: advanced search ------------------------------------------------------------------
/** A comma-separated query parameter, or an array, as a list. */
const listParam = (item) => zod_1.z.preprocess((value) => {
    if (typeof value === 'string')
        return value.split(',').map((part) => part.trim()).filter((part) => part !== '');
    return value ?? [];
}, zod_1.z.array(item).max(50));
exports.listParam = listParam;
exports.POOL_FILTERS = ['any_pool', 'no_pool'];
/** §L10 "Advanced search (POOL-04)". Every filter is optional and they combine with AND. */
exports.candidateSearchSchema = zod_1.z.object({
    /** Full text over the person, their form answers and their answers to job questions. */
    text: zod_1.z.string().trim().max(200).optional(),
    skills: (0, exports.listParam)(zod_1.z.string().trim().min(1).max(60)),
    /** "skills (all/any)". */
    skillMode: zod_1.z.enum(['all', 'any']).default('all'),
    minYears: zod_1.z.coerce.number().min(0).max(60).optional(),
    maxYears: zod_1.z.coerce.number().min(0).max(60).optional(),
    /** At least this level. */
    education: zod_1.z.enum(enums_1.EDUCATION_LEVELS).optional(),
    city: zod_1.z.string().trim().max(80).optional(),
    /** Whole taka a month, as the career form asks. */
    salaryMin: zod_1.z.coerce.number().int().min(0).max(999_999_999).optional(),
    salaryMax: zod_1.z.coerce.number().int().min(0).max(999_999_999).optional(),
    /** "last stage reached" — where the person's latest application got to. */
    stages: (0, exports.listParam)(zod_1.z.enum(enums_1.APPLICATION_STAGES)),
    sources: (0, exports.listParam)(zod_1.z.string().trim().min(1).max(40)),
    /** Applied between (any application). */
    from: calendars_1.isoDateSchema.optional(),
    to: calendars_1.isoDateSchema.optional(),
    pools: (0, exports.listParam)(schemas_1.uuidSchema),
    pool: zod_1.z.enum(exports.POOL_FILTERS).optional(),
    /** "availability": notice period of at most this many days. */
    maxNoticeDays: zod_1.z.coerce.number().int().min(0).max(365).optional(),
    /** S31 (ADMIN-13): a candidate custom field the viewer may see, and the text its value contains. */
    customField: schemas_1.uuidSchema.optional(),
    customValue: zod_1.z.string().trim().min(1).max(200).optional(),
    page: zod_1.z.coerce.number().int().positive().default(1),
    pageSize: zod_1.z.coerce.number().int().positive().max(100).default(50),
});
/** Where a terminal application got to: the stage it was at when it closed. */
function stageReached(stage, previousStage) {
    const isClosed = stage === 'rejected' || stage === 'withdrawn' || stage === 'closed_job' || stage === 'transferred';
    return isClosed && previousStage !== null ? previousStage : stage;
}
// --- F20.04: global search ---------------------------------------------------------------------
/** §L10: "Minimum 2 characters. Debounce 250ms." */
exports.SEARCH_MIN_CHARS = 2;
exports.SEARCH_DEBOUNCE_MS = 250;
/** §L10: "typeahead shows max 5 per category". */
exports.SEARCH_TYPEAHEAD_PER_TYPE = 5;
exports.SEARCH_TYPES = ['candidates', 'jobs', 'requisitions', 'pools'];
exports.SEARCH_TYPE_LABELS = {
    candidates: 'Candidates',
    jobs: 'Jobs',
    requisitions: 'Requisitions',
    pools: 'Talent pools',
};
/** §L10's ranking, best first: exact ID → name prefix → name fuzzy → skill tag → full text. */
exports.SEARCH_MATCH_KINDS = ['id', 'name_prefix', 'name', 'skill', 'text'];
exports.globalSearchQuerySchema = zod_1.z.object({
    q: zod_1.z.string().trim().min(exports.SEARCH_MIN_CHARS, `Type at least ${exports.SEARCH_MIN_CHARS} characters.`).max(120),
    /** `all` = the typeahead, five of each; one type = the results page's tab. */
    type: zod_1.z.enum(['all', ...exports.SEARCH_TYPES]).default('all'),
    page: zod_1.z.coerce.number().int().positive().default(1),
    pageSize: zod_1.z.coerce.number().int().positive().max(50).default(25),
    /** The results page records `search.executed`; the typeahead does not (ADR 0027). */
    record: zod_1.z.enum(['0', '1']).default('0'),
});
exports.referralReportQuerySchema = zod_1.z.object({
    from: calendars_1.isoDateSchema.optional(),
    to: calendars_1.isoDateSchema.optional(),
});
/** Hired means accepted or joined; not selected covers every rejection and a closed job. */
function referralOutcome(stage) {
    if (stage === 'joined' || stage === 'hired_pending_join')
        return 'hired';
    if (stage === 'withdrawn')
        return 'withdrawn';
    if (stage === 'rejected' || stage === 'auto_rejected' || stage === 'closed_job' || stage === 'transferred')
        return 'not_selected';
    return 'in_process';
}
//# sourceMappingURL=pools.js.map